import { SupabaseClient } from '@supabase/supabase-js';
import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';
import { z } from 'zod';

import { Database, Json, withLogs } from '@core';

import { FullLayout } from './layout';

export type LayoutCustomization = {
    id: number;
    parentUiNodeId: string | null;
    childUiNodeId: string;
    childDisplayOrder: number | null;
    /** {} = Do not override any of the child's metadata */
    childMetadata: Json | null;
    /** null = Do not override on what device the child is visible */
    showChildOn: ('kiosk' | 'floor')[] | null;
};

const layoutCustomizationSchema = z.object({
    id: z.number().optional(),
    parentUiNodeId: z.string().min(3).max(64).includes('.').nullable(),
    childUiNodeId: z.string().min(3).max(64).includes('.'),
    showChildOn: z.array(z.union([z.literal('kiosk'), z.literal('floor')])).nullable(),
    // `passthrough` is needed to allow passing unknown keys
    childMetadata: z.object({}).passthrough().nullable(),
    childDisplayOrder: z.number().nullable(),
});

const customizationsSchema = z.array(layoutCustomizationSchema);

export type ChildTypeFilter =
    | 'graph'
    | 'parameter'
    | 'module'
    | 'swing_foundations'
    | 'activity_navigation'
    | 'page'
    | 'graph_line';

export const fetchLayoutCustomizations = withLogs(
    async (
        supabaseClient: SupabaseClient<Database>,
        layoutId: number,
        childType?: ChildTypeFilter | ChildTypeFilter[],
    ): Promise<LayoutCustomization[]> => {
        let query = supabaseClient.from('layout_customizations').select('*').eq('layout_id', layoutId);

        if (childType) {
            if (Array.isArray(childType)) {
                // Build OR conditions like: child_ui_node_id.like.foo%,child_ui_node_id.like.bar%
                const orConditions = childType.map((ct) => `child_ui_node_id.like.${ct + '.'}%`).join(',');
                query = query.or(orConditions);
            } else {
                query = query.like('child_ui_node_id', `${childType + '.'}%`);
            }
        }

        const { data, error } = await query;

        if (error) {
            throw error;
        }

        return camelcaseKeys(data, { deep: true });
    },
    'fetchLayoutCustomizations',
);

export const fetchCustomModuleCustomizations = withLogs(
    async (
        supabaseClient: SupabaseClient<Database>,
        layoutId: number,
        moduleId: string,
    ): Promise<LayoutCustomization[]> => {
        const { data, error } = await supabaseClient
            .from('layout_customizations')
            .select('*')
            .eq('layout_id', layoutId)
            .or(`parent_ui_node_id.eq.${moduleId},child_ui_node_id.eq.${moduleId}`);

        if (error) {
            throw error;
        }

        return camelcaseKeys(data, { deep: true });
    },
    'fetchCustomModuleCustomizations',
);

export const omitCustomModuleCustomizations = withLogs(
    async (supabaseClient: SupabaseClient<Database>, layoutId: number, moduleId: string, parameterIds: string[]) => {
        if (parameterIds.length === 0) {
            return;
        }

        const { error } = await supabaseClient
            .from('layout_customizations')
            .delete()
            .eq('layout_id', layoutId)
            .eq('parent_ui_node_id', moduleId)
            .in('child_ui_node_id', parameterIds);

        if (error) {
            throw error;
        }
    },
    'omitCustomModuleCustomizations',
);

export const createLayoutCustomizations = withLogs(
    async (
        supabaseClient: SupabaseClient<Database>,
        layoutId: number,
        customizations: Omit<LayoutCustomization, 'id'>[],
    ): Promise<LayoutCustomization[]> => {
        const valid = customizationsSchema.parse(customizations);
        const payload = snakecaseKeys(valid, { deep: true }).map((customization) => ({
            ...customization,
            layout_id: layoutId,
            child_metadata: customization.child_metadata as Json | undefined,
        }));

        const { data, error } = await supabaseClient.from('layout_customizations').insert(payload).select('*');

        if (error) {
            throw error;
        }

        return camelcaseKeys(data, { deep: true });
    },
    'createLayoutCustomizations',
);

export const deleteLayoutCustomizations = withLogs(
    async (supabaseClient: SupabaseClient<Database>, layoutId: number, idsToDelete: number[]) => {
        const { error: deleteError } = await supabaseClient
            .from('layout_customizations')
            .delete()
            .eq('layout_id', layoutId)
            .in('id', idsToDelete);

        if (deleteError) {
            throw deleteError;
        }
    },
    'deleteLayoutCustomizations',
);

export const upsertLayoutCustomizations = withLogs(
    async (
        supabaseClient: SupabaseClient<Database>,
        layoutId: number,
        customizations: (LayoutCustomization | Omit<LayoutCustomization, 'id'>)[],
    ) => {
        const valid = customizationsSchema.parse(customizations);
        const payload = snakecaseKeys(valid, { deep: true }).map((customization) => ({
            ...customization,
            layout_id: layoutId,
            child_metadata: customization.child_metadata as Json,
        }));

        // supabase serializes all entries, so if some entries have id and others have none, they will have id set as null
        // and then we will have a posgres error of adding entries with null id.
        const [{ error: insertError }, { error: upsertError }] = await Promise.all([
            supabaseClient.from('layout_customizations').insert(payload.filter((p) => !p.id)),
            supabaseClient.from('layout_customizations').upsert(payload.filter((p) => !!p.id)),
        ]);

        if (insertError || upsertError) {
            throw insertError || upsertError;
        }
    },
    'upsertLayoutCustomizations',
);

export const copyLayoutCustomizations = withLogs(
    async (supabaseClient: SupabaseClient<Database>, sourceLayoutId: number, targetLayoutId: number) => {
        const { data: sourceCustomizations, error: fetchError } = await supabaseClient
            .from('layout_customizations')
            .select('*')
            .eq('layout_id', sourceLayoutId);

        if (fetchError) {
            throw fetchError;
        }

        if (!sourceCustomizations || sourceCustomizations.length === 0) {
            return [];
        }

        const newCustomizations = sourceCustomizations.map(({ id, layout_id, ...rest }) => ({
            ...rest,
            layout_id: targetLayoutId,
        }));

        const { data: targetCustomizations, error: insertError } = await supabaseClient
            .from('layout_customizations')
            .insert(newCustomizations)
            .select('*');

        if (insertError) {
            throw insertError;
        }

        return camelcaseKeys(targetCustomizations, { deep: true });
    },
    'copyLayoutCustomizations',
);

export type ModuleParameterUpdateBody = {
    moduleId: string;
    showChildOn?: 'kiosk' | 'floor' | null;
    parameterIds?: string[];
};

type ParametersUpdateBody = ModuleParameterUpdateBody & {
    layoutId: number;
};

export const parametersUpdate = withLogs(
    async (
        supabaseClient: SupabaseClient<Database>,
        { layoutId, moduleId, showChildOn, parameterIds }: ParametersUpdateBody,
    ) => {
        const { data: response, error } = await supabaseClient.functions.invoke<{ data: FullLayout }>(
            'parameters-update',
            {
                body: {
                    layoutId,
                    moduleId: moduleId,
                    showChildOn: showChildOn ?? null,
                    parameterIds: parameterIds,
                },
            },
        );

        if (error) {
            throw error;
        }

        return response?.data;
    },
);

type ParametersBatchUpdateBody = {
    layoutId: number;
    updates: ModuleParameterUpdateBody[];
};

export const parametersBatchUpdate = withLogs(
    async (supabaseClient: SupabaseClient<Database>, { layoutId, updates }: ParametersBatchUpdateBody) => {
        const { data: response, error } = await supabaseClient.functions.invoke<{ data: FullLayout }>(
            'parameters-batch-update',
            {
                body: {
                    layoutId,
                    updates,
                },
            },
        );

        if (error) {
            throw error;
        }

        return response?.data;
    },
);
